/**
 * THE parser for a scheduler error's structured code. One implementation,
 * shared by the hook that normalizes thrown request errors
 * (`useSchedulerGeneration.toSchedulerError`) and the wizard page that routes
 * the failure screen off the code — they must never disagree about what the
 * backend said.
 *
 * The two routes report the SAME failure in two different shapes, so all three
 * keys are load-bearing:
 *
 *   POST .../generate  → `{ error: CODE, message, detail: { code, ... } }`
 *                        (SchedulerController catches it itself)
 *   POST .../retry     → `{ status, message, error_code: CODE }`
 *                        (no bespoke catch; the global renderer in
 *                         api2/bootstrap/app.php emits `error_code`)
 *
 * `req()` throws 4xx bodies verbatim as a PLAIN OBJECT (see
 * common/api/request.js — it replaces the Error with the parsed JSON), and
 * nests bodies under `bodyData` only for status > 500.
 */
export function extractSchedulerErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') {
    return null
  }
  const source = err as {
    error?: unknown
    error_code?: unknown
    detail?: { code?: unknown }
    bodyData?: {
      error?: unknown
      error_code?: unknown
      detail?: { code?: unknown }
    }
  }
  const body = source.bodyData ?? source
  const candidate = body.error_code ?? body.detail?.code ?? body.error

  // `error` doubles as a lowercase slug on some payloads ('unknown_slot_uid',
  // 'pre_fix_flight'); only SCREAMING_SNAKE values are scheduler error codes.
  return typeof candidate === 'string' && /^[A-Z][A-Z0-9_]*$/.test(candidate)
    ? candidate
    : null
}

/**
 * The structured fields api2's exception renderer forwards alongside the code.
 *
 * `req()` throws a 4xx body verbatim as a plain object but nests a >500 body
 * under `bodyData`, so the season allocator's structured 503
 * (`{status, message, error_code, stage, retryable}`) arrives one level down.
 * Reading only the top level left the page with the raw
 * `HTTP Status: 503 … URL: http://scheduler-python:8000/…` string as its
 * headline and no code to route on.
 */
export interface SchedulerErrorFields {
  code: string | null
  message: string | null
  stage: string | null
  /** `null` when the backend did not state it — NOT the same as `false`. */
  retryable: boolean | null
}

/**
 * Pull the structured scheduler fields out of whatever `req()` threw.
 *
 * Shares `extractSchedulerErrorCode` for the code so the two can never
 * disagree about what the backend said.
 */
export function extractSchedulerErrorFields(
  err: unknown
): SchedulerErrorFields {
  const empty: SchedulerErrorFields = {
    code: null,
    message: null,
    stage: null,
    retryable: null,
  }
  if (!err || typeof err !== 'object') {
    return empty
  }
  const source = err as {
    message?: unknown
    stage?: unknown
    retryable?: unknown
    detail?: { message?: unknown; stage?: unknown; retryable?: unknown }
    bodyData?: {
      message?: unknown
      stage?: unknown
      retryable?: unknown
      detail?: { message?: unknown; stage?: unknown; retryable?: unknown }
    }
  }
  // For a >500 the top-level `message` is req's own
  // "HTTP Status: … - URL: …" string, so ONLY the nested body is a safe
  // message source there. For a 4xx the body IS the top level.
  const body = source.bodyData ?? source
  const detail = body.detail ?? {}

  const message = [detail.message, body.message].find(
    (m): m is string => typeof m === 'string' && m.trim() !== ''
  )
  const stage = [detail.stage, body.stage].find(
    (v): v is string => typeof v === 'string' && v.trim() !== ''
  )
  const retryable = [detail.retryable, body.retryable].find(
    (v): v is boolean => typeof v === 'boolean'
  )

  return {
    code: extractSchedulerErrorCode(err),
    message: message ?? null,
    stage: stage ?? null,
    retryable: retryable ?? null,
  }
}
